import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import "react-calendar/dist/Calendar.css";
import Icon from "../../../components/Icon";
import ContainerBox from "../../../components/ContainerBox";
import styled from "styled-components";
import { Typography, Link } from "@mui/material";
import { Circle } from "../../../components/Overlay";
import AddFeatureModal from "./components/AddFeatureModal";
import VariantList from "./components/VariantList";
import FeatureList from "./components/FeatureList";
import useGetMachineFeatureMapping from "../../../hooks/useGetMachineFeatureMapping";
import CopyFromModal from "./components/CopyFromModal";
import { get } from "lodash";
import Button from "../../../components/Button";
import UploadFile from "./components/UploadFile";
import {
  addVariantFeatures,
  disableVariantFeature,
  enableVariantFeature,
  updateFeatureDetail,
  uploadModelDoc,
  uploadVariantImage,
} from "../../../actions/machine-action-types";
import { toast } from "react-toastify";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import AlertModal from "./components/AlertModal";
import Grid from "@mui/material/Grid";

////********Styled Components ********************/
const MainContainer = styled.div`
  padding: 20px 40px;
  overflow: hidden;
  input[type="file"] {
    color: transparent !important;
    width: 100px;
  }
`;

const BackContainer = styled.div`
  //background: #003e85;
  display: flex;
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: absolute;
  top: 20px;
  left: 0px;
`;

const LoaderContainer = styled.div`
  display: flex;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  justify-content: center;
  background-color: rgb(0, 0, 0, 0.3);
  align-items: center;
  z-index: 999;
`;

const ImageContainer = styled.div`
  border: 1px dashed #cacaca;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 8px;
  position: relative;
  height: 100%;
  width: 250px;
  min-height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModelDetails = ({
  match,
  userData,
  visible,
  uploadVariantImage,
  uploadModelDoc,
  updateFeatureDetail,
  addVariantFeatures,
  disableVariantFeature,
  enableVariantFeature,
  ...props
}) => {
  const [showAddFeature, setShowAddFeature] = useState(false);
  const [showEditFeature, setShowEditFeature] = useState(false);
  const [showCopyFrom, setShowCopyFrom] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [copyFeatures, setCopyFeatures] = useState([]);
  const [showUploadManual, setShowUploadManual] = useState(false);
  const [showUploadImage, setShowUploadImage] = useState(false);
  const [showUploadModelImage, setShowUploadModelImage] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState({ id: 0, name: "" });
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState({});

  let history = useHistory();
  const { modelName } = match.params;

  const { fetchMachineFeatureMapping, getModelDetails, modelsList } =
    useGetMachineFeatureMapping();

  useEffect(() => {
    fetchMachineFeatureMapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modelDetails = getModelDetails(modelName);

  const variantFeatures = useMemo(() => {
    const variants = modelDetails?.variants ?? [];
    const features =
      variants?.find((val) => val.variantId === selectedVariant.id)?.features ??
      [];

    return features;
  }, [modelDetails, selectedVariant]);

  // Add Variant Features
  const handleAddFeatures = (values) => {
    const featuresToAdd = values?.features?.map((val) => {
      return {
        groupId: val.groupId,
        featureId: val.featureId,
        detail: val.value,
        modelName: modelName,
        modelDesc: modelDetails?.modelDesc,
        operatorManualPdfUrl: modelDetails?.operatorManualPdfUrl,
        equipmentUrl: modelDetails?.equipmentUrl,
      };
    });
    addVariantFeatures({
      params: {
        userId: userData?.user_id ?? "ADMIN",
        variantId: selectedVariant.id,
        featureMapList: featuresToAdd,
      },
      onSuccess: (response) => {
        setShowAddFeature(false);
        toast.success("Feature Added Successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
        fetchMachineFeatureMapping();
      },
      onError: (error) => {
        const errorMsg =
          get(error, "error.response.data.message") ??
          "Something went wrong, Please try again later.";
        toast.error(errorMsg, {
          position: toast.POSITION.TOP_RIGHT,
        });
      },
    });
  };

  // Update Variant Feature
  const handleUpdateFeature = (values) => {
    const featureObj = get(values, "features[0]") ?? {};
    updateFeatureDetail({
      params: {
        userId: userData?.user_id ?? "ADMIN",
        grpFeatureDtlId: featureObj?.grpFeatureDtlId,
        detail: featureObj?.value,
      },
      onSuccess: (response) => {
        setShowEditFeature(false);
        toast.success("Feature Updated Successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
        fetchMachineFeatureMapping();
      },
      onError: (error) => {
        const errorMsg =
          get(error, "error.response.data.message") ??
          "Something went wrong, Please try again later.";
        toast.error(errorMsg, {
          position: toast.POSITION.TOP_RIGHT,
        });
      },
    });
  };

  // Upload Variant Image
  const handleUploadVariantImage = (data) => {
    // Create a new FormData object
    const formData = new FormData();
    formData.append("file", data); // Append the file
    formData.append("variantId", selectedVariant.id); // Append the variantID
    formData.append("userId", userData?.user_id ?? "ADMIN"); // Append the userId
    uploadVariantImage({
      params: formData,
      onSuccess: (response) => {
        setShowUploadImage(false);
        setSelectedVariant({});
        toast.success("Image Uploaded Successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
        fetchMachineFeatureMapping();
      },
      onError: (error) => {
        const errorMsg =
          get(error, "error.response.data.message") ??
          "Something went wrong, Please try again later.";
        toast.error(errorMsg, {
          position: toast.POSITION.TOP_RIGHT,
        });
      },
    });
  };

  // Upload Operator Manual
  const handleUploadOperatorManual = (data) => {
    // Create a new FormData object
    const formData = new FormData();
    formData.append("file", data); // Append the file
    formData.append("modelName", modelName); // Append the variantID
    formData.append("userId", userData?.user_id ?? "ADMIN"); // Append the userId
    formData.append("isFor", "OperatorManual"); // Append isFor to identify that doc is upload for operator manual
    uploadModelDoc({
      params: formData,
      onSuccess: (response) => {
        setShowUploadManual(false);
        toast.success("Operator Manual Uploaded Successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
        fetchMachineFeatureMapping();
      },
      onError: (error) => {
        const errorMsg =
          get(error, "error.response.data.message") ??
          "Something went wrong, Please try again later.";
        toast.error(errorMsg, {
          position: toast.POSITION.TOP_RIGHT,
        });
      },
    });
  };

  // Upload Model Default Image
  const handleUploadModelDefaultImage = (data) => {
    // Create a new FormData object
    const formData = new FormData();
    formData.append("file", data); // Append the file
    formData.append("modelName", modelName); // Append the variantID
    formData.append("userId", userData?.user_id ?? "ADMIN"); // Append the userId
    formData.append("isFor", "modelImage"); // Append isFor to identify that doc is upload for model image
    uploadModelDoc({
      params: formData,
      onSuccess: (response) => {
        setShowUploadModelImage(false);
        toast.success("Machine Model Image Uploaded Successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
        fetchMachineFeatureMapping();
      },
      onError: (error) => {
        const errorMsg =
          get(error, "error.response.data.message") ??
          "Something went wrong, Please try again later.";
        toast.error(errorMsg, {
          position: toast.POSITION.TOP_RIGHT,
        });
      },
    });
  };

  // Disable Variant Feature
  const handleFeatureDisable = () => {
    disableVariantFeature({
      params: {
        userId: userData?.user_id ?? "ADMIN",
        grpFeatureVariantId: selectedFeature.grpFeatureVariantId,
      },
      onSuccess: (response) => {
        setShowInactiveModal(false);
        toast.success("Feature Disable Successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
        fetchMachineFeatureMapping();
      },
      onError: (error) => {
        const errorMsg =
          get(error, "error.response.data.message") ??
          "Something went wrong, Please try again later.";
        toast.error(errorMsg, {
          position: toast.POSITION.TOP_RIGHT,
        });
      },
    });
  };

  // Enable Variant Feature
  const handleFeatureEnable = () => {
    enableVariantFeature({
      params: {
        userId: userData?.user_id ?? "ADMIN",
        grpFeatureVariantId: selectedFeature.grpFeatureVariantId,
        grpFeatureId: selectedFeature.grpFeatureId,
        variantId: selectedVariant.id,
        enabled: true,
      },
      onSuccess: (response) => {
        setShowActivateModal(false);
        toast.success("Feature Enable Successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
        fetchMachineFeatureMapping();
      },
      onError: (error) => {
        const errorMsg =
          get(error, "error.response.data.message") ??
          "Something went wrong, Please try again later.";
        toast.error(errorMsg, {
          position: toast.POSITION.TOP_RIGHT,
        });
      },
    });
  };

  return (
    <MainContainer>
      <BackContainer onClick={() => history.goBack()}>
        <Icon icon="backIcon" color={"#000"} />
      </BackContainer>
      <Typography variant="h5" fontWeight={700} color={"#003E85"}>
        {`${modelName} Details`}
      </Typography>
      <ContainerBox label={"Information"}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="body1" fontWeight={700}>
                  {"Model Name:"}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body1">
                  {modelDetails.modelName ?? "-"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body1" fontWeight={700}>
                  {"Model Description:"}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body1">
                  {modelDetails.modelDesc ?? "-"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                {modelDetails?.operatorManualPdfUrl ? (
                  <Link
                    href={modelDetails?.operatorManualPdfUrl}
                    target="_blank"
                    variant="body1"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {"Operator Manual"}
                    <OpenInNewIcon sx={{ fontSize: 20, marginLeft: 1 }} />
                  </Link>
                ) : (
                  <Typography variant="body1">{"Operator Manual"}</Typography>
                )}
              </Grid>
              <Grid item xs={8}>
                <Button
                  label={"Upload"}
                  height={"30px"}
                  onClick={() => {
                    setShowUploadManual(true);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={6}
            height={150}
            style={{
              display: "flex",
              justifyContent: "end",
            }}
          >
            <ImageContainer>
              <img
                src={modelDetails.productImage}
                style={{
                  width: "100%",
                  objectFit: "contain",
                  height: "100%",
                  backgroundColor: "#F4F4F4",
                  cursor: "zoom-in",
                }}
                alt={"img"}
                onLoad={() => {}}
                onClick={() => {}}
              />
              <div
                style={{
                  position: "absolute",
                  right: 6,
                  top: 6,
                  cursor: "pointer",
                  display: "flex",
                  padding: 4,
                }}
                onClick={() => {
                  setShowUploadModelImage(true);
                }}
              >
                <FileUploadOutlinedIcon />
              </div>
            </ImageContainer>
          </Grid>
        </Grid>
      </ContainerBox>
      <ContainerBox label={"Variants"}>
        <VariantList
          variants={modelDetails.variants}
          showFeatures={(variantId, variantName) => {
            setSelectedVariant({ id: variantId, name: variantName });
            setShowFeatures(true);
          }}
          onImageUpload={(variantId, variantName) => {
            setSelectedVariant({ id: variantId, name: variantName });
            setShowUploadImage(true);
          }}
        />
      </ContainerBox>

      {visible && (
        <LoaderContainer>
          <Circle size="Large" />
        </LoaderContainer>
      )}
      {(showUploadImage || showUploadModelImage || showUploadManual) && (
        <UploadFile
          fileType={showUploadImage || showUploadModelImage ? "image" : "pdf"}
          onSubmit={(file) => {
            // if File size greater 100 mb
            if (file?.size > 100000000) {
              toast.error("File size exceeds maximum limit 100 MB.", {
                position: toast.POSITION.TOP_RIGHT,
              });
              return;
            }
            if (showUploadImage) {
              handleUploadVariantImage(file);
            } else if (showUploadModelImage) {
              handleUploadModelDefaultImage(file);
            } else if (showUploadManual) {
              handleUploadOperatorManual(file);
            }
          }}
          onCancel={() => {
            setSelectedVariant({});
            setShowUploadImage(false);
            setShowUploadManual(false);
            setShowUploadModelImage(false);
          }}
        />
      )}
      {showFeatures && (
        <FeatureList
          selectedVariant={selectedVariant}
          features={variantFeatures}
          onCancel={() => {
            setShowFeatures(false);
            setSelectedVariant({});
          }}
          onEdit={(features) => {
            setCopyFeatures(features);
            setShowEditFeature(true);
          }}
          onDelete={(values) => {
            setSelectedFeature(values);
            setShowInactiveModal(true);
          }}
          onActivate={(values) => {
            setSelectedFeature(values);
            setShowActivateModal(true);
          }}
          onAddNew={() => setShowAddFeature(true)}
          onCopy={() => setShowCopyFrom(true)}
        />
      )}
      {(showAddFeature || showEditFeature) && (
        <AddFeatureModal
          addedFeatures={variantFeatures}
          featureData={copyFeatures}
          isEditMode={showEditFeature}
          onSubmit={showEditFeature ? handleUpdateFeature : handleAddFeatures}
          onCancel={() => {
            setCopyFeatures([]);
            setShowEditFeature(false);
            setShowAddFeature(false);
          }}
        />
      )}
      {showCopyFrom && (
        <CopyFromModal
          models={modelsList}
          selectedVariant={selectedVariant}
          onCopy={(features) => {
            setCopyFeatures(features);
            setShowCopyFrom(false);
            setShowAddFeature(true);
          }}
          onCancel={() => {
            setCopyFeatures([]);
            setShowCopyFrom(false);
          }}
        />
      )}
      {showInactiveModal && (
        <AlertModal
          title={"Disable Feature"}
          message={`Are you sure, you want to disable this feature for variant ${selectedVariant.name}`}
          confirmTitle={"Disable"}
          onSubmit={() => handleFeatureDisable()}
          onCancel={() => setShowInactiveModal(false)}
        />
      )}
      {showActivateModal && (
        <AlertModal
          title={"Enable Feature"}
          message={`Are you sure, you want to enable this feature for variant ${selectedVariant.name}`}
          confirmTitle={"Activate"}
          onSubmit={() => handleFeatureEnable()}
          onCancel={() => setShowActivateModal(false)}
        />
      )}
    </MainContainer>
  );
};

const mapStateToProps = ({ app: { visible }, user: { userData } }) => ({
  userData,
  visible,
});

export default connect(mapStateToProps, {
  uploadVariantImage: uploadVariantImage,
  uploadModelDoc: uploadModelDoc,
  addVariantFeatures: addVariantFeatures,
  updateFeatureDetail: updateFeatureDetail,
  disableVariantFeature: disableVariantFeature,
  enableVariantFeature: enableVariantFeature,
})(ModelDetails);
